import { isProxy, toRaw } from "vue";
import { DateTime } from "luxon";

function cloneValue<TValue>(value: TValue, active: WeakSet<object>): TValue {
  if (value == null || typeof value !== "object") {
    return value;
  }

  const rawValue = isProxy(value) ? toRaw(value) : value;

  if (rawValue instanceof Date) {
    return new Date(rawValue.getTime()) as TValue;
  }

  if (DateTime.isDateTime(rawValue)) {
    // Luxon DateTime values are immutable, so retaining the instance is a stable snapshot.
    return rawValue as TValue;
  }

  if (!Array.isArray(rawValue) && Object.getPrototypeOf(rawValue) !== Object.prototype) {
    throw new TypeError(
      "Validation baselines only support arrays, plain objects, Date, and Luxon DateTime values",
    );
  }

  if (active.has(rawValue)) {
    throw new TypeError("Cyclic validation values are not supported");
  }

  active.add(rawValue);
  try {
    if (Array.isArray(rawValue)) {
      return rawValue.map((entry) => cloneValue(entry, active)) as TValue;
    }

    const clone: Record<string, unknown> = {};
    Object.entries(rawValue).forEach(([key, entry]) => {
      clone[key] = cloneValue(entry, active);
    });
    return clone as TValue;
  } finally {
    active.delete(rawValue);
  }
}

export function cloneValidationValue<TValue>(value: TValue): TValue {
  return cloneValue(value, new WeakSet());
}
